import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { User, FileText, CheckCircle2, Loader2, Building2, MapPin } from "lucide-react";
import { z } from "zod";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Progress } from "@/components/ui/progress";

import { CountryService } from "@/services/countryService";
import { USOnboardingRequest, USOnboardingResponse } from "@/types/country";
import { AddressForm } from "@/components/i18n";
import { useCountry } from "@/contexts/CountryContext";
import DashboardContainer from "../layout/DashboardContainer";

// US-specific validation schemas
const einSchema = z.string().regex(/^\d{2}-\d{7}$/, "EIN must be in format XX-XXXXXXX");
const ssnSchema = z.string().regex(/^\d{3}-\d{2}-\d{4}$/, "SSN must be in format XXX-XX-XXXX");

const usOnboardingSchema = z.object({
  legalName: z.string().min(2, "Legal name must be at least 2 characters"),
  businessType: z.enum(["individual", "llc", "corporation", "partnership"], {
    required_error: "Please select a business type",
  }),
  einOrSsn: z.string().min(9, "EIN or SSN is required"),
  taxIdType: z.enum(["ein", "ssn"], {
    required_error: "Please select tax ID type",
  }),
  w9Acknowledged: z.boolean().refine((val) => val === true, {
    message: "You must acknowledge the W-9 requirement",
  }),
  address: z.object({
    line1: z.string().min(1, "Address line 1 is required"),
    line2: z.string().optional(),
    city: z.string().min(1, "City is required"),
    state: z.string().min(2, "State is required"),
    zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, "ZIP code must be in format 12345 or 12345-6789"),
    country: z.string().default("United States"),
  }),
}).superRefine((data, ctx) => {
  // Validate EIN/SSN based on selected type
  if (data.taxIdType === "ein") {
    if (!einSchema.safeParse(data.einOrSsn).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "EIN must be in format XX-XXXXXXX",
        path: ["einOrSsn"],
      });
    }
  } else if (data.taxIdType === "ssn") {
    if (!ssnSchema.safeParse(data.einOrSsn).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "SSN must be in format XXX-XX-XXXX",
        path: ["einOrSsn"],
      });
    }
  }
});

type USOnboardingFormData = z.infer<typeof usOnboardingSchema>;

const steps = [
  {
    id: "business-info",
    title: "Business Information",
    description: "Tell us about your business",
    icon: Building2,
  },
  {
    id: "tax-id",
    title: "Tax Information",
    description: "Provide your EIN or SSN",
    icon: FileText,
  },
  {
    id: "address",
    title: "Address Information",
    description: "Enter your business address",
    icon: MapPin,
  },
  {
    id: "w9-acknowledgment",
    title: "W-9 Acknowledgment",
    description: "Review and acknowledge W-9 requirements",
    icon: CheckCircle2,
  },
  {
    id: "confirmation",
    title: "Confirmation",
    description: "Review and submit your information",
    icon: User,
  },
];

interface USOnboardingWizardProps {
  contractorId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const USOnboardingWizard: React.FC<USOnboardingWizardProps> = ({
  contractorId,
  onSuccess,
  onCancel,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { setCurrentCountryCode } = useCountry();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<USOnboardingFormData>({
    resolver: zodResolver(usOnboardingSchema),
    defaultValues: {
      legalName: "",
      businessType: "individual",
      einOrSsn: "",
      taxIdType: "ssn",
      w9Acknowledged: false,
      address: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        zipCode: "",
        country: "United States",
      },
    },
    mode: "onChange",
  });

  // Auto-format EIN/SSN as user types
  const handleTaxIdChange = (value: string, field: any) => {
    const taxIdType = form.watch("taxIdType");
    let formatted = value.replace(/\D/g, ""); // Remove non-digits
    
    if (taxIdType === "ein" && formatted.length >= 2) {
      formatted = formatted.slice(0, 9);
      formatted = formatted.replace(/(\d{2})(\d{1,7})/, "$1-$2");
    } else if (taxIdType === "ssn" && formatted.length >= 3) {
      formatted = formatted.slice(0, 9);
      formatted = formatted.replace(/(\d{3})(\d{1,2})?(\d{1,4})?/, (match, g1, g2, g3) => {
        let result = g1;
        if (g2) result += `-${g2}`;
        if (g3) result += `-${g3}`;
        return result;
      });
    }
    
    field.onChange(formatted);
  };

  const validateStep = async (step: number) => {
    let fieldsToValidate: string[] = [];
    
    switch (step) {
      case 0:
        fieldsToValidate = ["legalName", "businessType"];
        break;
      case 1:
        fieldsToValidate = ["taxIdType", "einOrSsn"];
        break;
      case 2:
        fieldsToValidate = ["address.line1", "address.city", "address.state", "address.zipCode"];
        break;
      case 3:
        fieldsToValidate = ["w9Acknowledged"];
        break;
      default:
        return true;
    }
    
    return await form.trigger(fieldsToValidate as any);
  };

  const nextStep = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (!isValid) {
      toast({
        title: "Please fix validation errors",
        description: "Check the form fields and try again.",
        variant: "destructive",
      });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async (data: USOnboardingFormData) => {
    setIsSubmitting(true);
    try {
      const requestData: USOnboardingRequest = {
        contractorId: contractorId || "temp-id", // TODO: Get actual contractor ID
        legalName: data.legalName,
        einOrSsn: data.einOrSsn,
        taxIdType: data.taxIdType,
        w9Acknowledged: data.w9Acknowledged,
        businessType: data.businessType,
        address: data.address,
      };

      const response = await CountryService.submitUSOnboarding(requestData);
      
      if (response.success) {
        // Set country context to US
        setCurrentCountryCode('US');
        
        toast({
          title: "Success!",
          description: "Your US contractor profile has been created successfully.",
        });

        if (onSuccess) {
          onSuccess();
        } else {
          navigate("/contractor/dashboard");
        }
      } else {
        throw new Error(response.message || "Failed to submit onboarding data");
      }
    } catch (error: any) {
      console.error("US onboarding submission error:", error);
      toast({
        title: "Submission Failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      await onSubmit(form.getValues());
    }
  };

  const renderBusinessInfoStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Business Information</CardTitle>
        <CardDescription>
          Tell us about your business structure and legal name.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={form.control}
          name="legalName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Legal Name *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Enter your full legal name or business name"
                  data-testid="us-onboarding-legal-name"
                />
              </FormControl>
              <FormDescription>
                This should match the name on your tax documents.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="businessType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Business Type *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger data-testid="us-onboarding-business-type">
                    <SelectValue placeholder="Select your business type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="individual">Individual/Sole Proprietor</SelectItem>
                  <SelectItem value="llc">LLC</SelectItem>
                  <SelectItem value="corporation">Corporation</SelectItem>
                  <SelectItem value="partnership">Partnership</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Choose the business structure that matches your tax filing status.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );

  const renderTaxIdStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Tax Information</CardTitle>
        <CardDescription>
          Provide your Employer Identification Number (EIN) or Social Security Number (SSN).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={form.control}
          name="taxIdType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tax ID Type *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger data-testid="us-onboarding-tax-id-type">
                    <SelectValue placeholder="Select tax ID type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="ssn">Social Security Number (SSN)</SelectItem>
                  <SelectItem value="ein">Employer Identification Number (EIN)</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Individual contractors typically use SSN. Businesses use EIN.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="einOrSsn"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {form.watch("taxIdType") === "ein" ? "EIN" : "SSN"} *
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={
                    form.watch("taxIdType") === "ein" 
                      ? "XX-XXXXXXX" 
                      : "XXX-XX-XXXX"
                  }
                  onChange={(e) => handleTaxIdChange(e.target.value, field)}
                  data-testid="us-onboarding-ein-ssn"
                />
              </FormControl>
              <FormDescription>
                {form.watch("taxIdType") === "ein" 
                  ? "Enter your 9-digit EIN in format XX-XXXXXXX"
                  : "Enter your 9-digit SSN in format XXX-XX-XXXX"
                }
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Alert>
          <FileText className="h-4 w-4" />
          <AlertTitle>Security Notice</AlertTitle>
          <AlertDescription>
            Your tax information is encrypted and stored securely. We only use this 
            information for tax reporting purposes as required by the IRS.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );

  const renderAddressStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Address Information</CardTitle>
        <CardDescription>
          Enter your business address for tax reporting purposes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AddressForm
          address={form.watch("address")}
          onChange={(address) => {
            Object.entries(address).forEach(([key, value]) => {
              form.setValue(`address.${key}` as any, value);
            });
          }}
          forceCountry="US"
        />
      </CardContent>
    </Card>
  );

  const renderW9Step = () => (
    <Card>
      <CardHeader>
        <CardTitle>W-9 Acknowledgment</CardTitle>
        <CardDescription>
          Please review and acknowledge the W-9 tax form requirements.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <FileText className="h-4 w-4" />
          <AlertTitle>W-9 Form Requirement</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              As a US contractor, you are required to provide W-9 information for tax reporting. 
              This includes:
            </p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Your legal name and business name (if different)</li>
              <li>Your tax identification number (SSN or EIN)</li>
              <li>Your address information</li>
              <li>Your tax classification (business type)</li>
            </ul>
            <p className="mt-2">
              At the end of the tax year, you will receive a 1099-NEC form if you earn 
              $600 or more from clients using this platform.
            </p>
          </AlertDescription>
        </Alert>

        <FormField
          control={form.control}
          name="w9Acknowledged"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="us-onboarding-w9-acknowledgment"
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  I acknowledge the W-9 requirements *
                </FormLabel>
                <FormDescription>
                  I understand that I am required to provide accurate W-9 information 
                  and that this information will be used for tax reporting purposes.
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
        <FormMessage />
      </CardContent>
    </Card>
  );

  const renderConfirmationStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Confirmation</CardTitle>
        <CardDescription>
          Please review your information before submitting.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-sm text-muted-foreground">Legal Name</h4>
            <p>{form.watch("legalName")}</p>
          </div>
          <div>
            <h4 className="font-medium text-sm text-muted-foreground">Business Type</h4>
            <p className="capitalize">{form.watch("businessType")}</p>
          </div>
          <div>
            <h4 className="font-medium text-sm text-muted-foreground">Tax ID Type</h4>
            <p>{form.watch("taxIdType").toUpperCase()}</p>
          </div>
          <div>
            <h4 className="font-medium text-sm text-muted-foreground">Tax ID</h4>
            <p>{form.watch("einOrSsn")}</p>
          </div>
        </div>
        
        <div>
          <h4 className="font-medium text-sm text-muted-foreground mb-1">Address</h4>
          <div className="text-sm">
            <p>{form.watch("address.line1")}</p>
            {form.watch("address.line2") && <p>{form.watch("address.line2")}</p>}
            <p>
              {form.watch("address.city")}, {form.watch("address.state")} {form.watch("address.zipCode")}
            </p>
            <p>{form.watch("address.country")}</p>
          </div>
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-center space-x-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span className="text-sm">W-9 requirements acknowledged</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return renderBusinessInfoStep();
      case 1:
        return renderTaxIdStep();
      case 2:
        return renderAddressStep();
      case 3:
        return renderW9Step();
      case 4:
        return renderConfirmationStep();
      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <DashboardContainer title="US Contractor Onboarding">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Progress indicator */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Step {currentStep + 1} of {steps.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="w-full" />
        </div>

        {/* Step indicators */}
        <div className="flex justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <div
                key={step.id}
                className={`flex flex-col items-center space-y-2 ${
                  isActive ? "text-primary" : isCompleted ? "text-green-500" : "text-muted-foreground"
                }`}
              >
                <div
                  className={`rounded-full p-2 ${
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : isCompleted 
                      ? "bg-green-500 text-white" 
                      : "bg-muted"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-center">
                  <div className="text-xs font-medium">{step.title}</div>
                  <div className="text-xs text-muted-foreground hidden sm:block">
                    {step.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Form content */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {renderStep()}

            {/* Navigation buttons */}
            <div className="flex justify-between">
              <div>
                {onCancel && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                )}
                {currentStep > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    disabled={isSubmitting}
                    className="ml-2"
                  >
                    Previous
                  </Button>
                )}
              </div>
              
              <div>
                {currentStep === steps.length - 1 ? (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    data-testid="us-onboarding-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Complete Setup"
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={isSubmitting}
                    data-testid="us-onboarding-next"
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Form>
      </div>
    </DashboardContainer>
  );
};

export default USOnboardingWizard; 